import { IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonButton, IonIcon } from "@ionic/react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ToolfinderStandardParameterForm } from "./FormTypes";
import { arrowDownOutline, createOutline } from "ionicons/icons";
import { InputAdornment, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getMaxAPValue } from "./FormHelpers";
import { useTranslation } from "react-i18next";

interface StandardParameterFormProps {
    isFormShowing: boolean,
    submitForm: (data: any) => void,
    strategy: string | undefined,
    editBtnCallback: () => void,
    formData: ToolfinderStandardParameterForm | undefined,
}

const defaultFormValues = {
    diameter: "",
    ae: "",
    ap: "",
}

const StandardParameterForm: React.FC<StandardParameterFormProps> = ({ isFormShowing, submitForm, strategy, editBtnCallback, formData }: StandardParameterFormProps) => {
    const [data, setData] = useState<ToolfinderStandardParameterForm | undefined>()
    const { control, reset, handleSubmit, formState: { errors }, setValue, watch } = useForm<ToolfinderStandardParameterForm>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: defaultFormValues
    })
    const { t } = useTranslation();

    const selectedDiameter = watch("diameter")
    const selectedAe = watch("ae")

    useEffect(() => {
        reset()
    }, [strategy, reset])

    useEffect(() => {
        if (formData === undefined) {
            setData(undefined)
        }
    }, [formData])

    const onSubmit: SubmitHandler<ToolfinderStandardParameterForm> = data => {
        submitForm(data)
        setData(data)
    }

    const onDiameterChange = (diameter: any) => {
        setValue("diameter", diameter)

        if (diameter !== "") {
            if (strategy === "std-vsp") {
                setValue("ae", diameter)
            } else if (strategy === "std-ko") {
                setValue("ae", diameter * 0.03)
            }
        }
    }

    if (isFormShowing) {
        return (
            <IonCard>
                <IonCardHeader>
                    <IonCardTitle>{t("toolfinder.Parameter")}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                    <form>
                        {/* Diameter */}
                        <div>
                            <Controller
                                control={control}
                                name="diameter"
                                rules={{
                                    required: { value: true, message: t("Feld darf nicht leer sein") },
                                    min: { value: 0.1, message:  t("toolfinder.StdParameterForm.minD", {param: "0.1 mm"}) },
                                    max: { value: 25, message: t("toolfinder.StdParameterForm.maxD", {param: "25 mm"}) },
                                }}
                                render={({ field: { value, onBlur } }) => (
                                    <TextField
                                        variant="standard"
                                        label={t("toolfinder.StdParameterForm.Werkzeugdurchmesser")}
                                        value={value}
                                        onChange={(e) => onDiameterChange(e.target.value)}
                                        onBlur={onBlur}
                                        type="number"
                                        style={{ width: "100%" }}
                                        slotProps={{
                                            input: {
                                                endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                                            }
                                        }}
                                    />
                                )}
                            />

                            {/* @ts-ignore */}
                            {errors.diameter && <div className="error">{errors.diameter.message}</div>}
                        </div>

                        <div style={{ display: "flex", gap: 30, marginTop: 10 }}>
                            {/* ae */}
                            <div>
                                <Controller
                                    control={control}
                                    name="ae"
                                    rules={{
                                        required: { value: true, message: t("Feld darf nicht leer sein") },
                                        min: { value: 0.1, message: t("toolfinder.StdParameterForm.valueTooSmall") },
                                        max: { value: strategy && strategy === "std-bes" ? Number(selectedDiameter) * 0.99 : selectedDiameter, message: t("toolfinder.StdParameterForm.valueTooBig") }
                                    }}
                                    render={({ field: { value, onChange, onBlur } }) => (
                                        <TextField
                                            variant="standard"
                                            label={<Typography>a<sub>e</sub></Typography>}
                                            value={value}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            type="number"
                                            disabled={strategy === "std-vsp" || strategy === "std-ko"}
                                            slotProps={{
                                                input: {
                                                    endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                                                }
                                            }}
                                        />
                                    )}
                                />

                                {/* @ts-ignore */}
                                {errors.ae && <div className="error">{errors.ae.message}</div>}
                            </div>

                            {/* a_p */}
                            <div>
                                <Controller
                                    control={control}
                                    name="ap"
                                    rules={{
                                        required: { value: true, message: t("Feld darf nicht leer sein") },
                                        min: { value: 0.1, message: t("toolfinder.StdParameterForm.valueTooSmall") },
                                        max: { value: strategy !== undefined ? getMaxAPValue(strategy, Number(selectedDiameter), Number(selectedAe)).value : 100, message: t("toolfinder.StdParameterForm.valueTooBig") }
                                    }}
                                    render={({ field: { value, onChange, onBlur } }) => (
                                        <TextField
                                            variant="standard"
                                            label={<Typography>a<sub>p</sub></Typography>}
                                            value={value}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            type="number"
                                            slotProps={{
                                                input: {
                                                    endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                                                }
                                            }}
                                        />
                                    )}
                                />

                                {/* @ts-ignore */}
                                {errors.ap && <div className="error">{errors.ap.message}</div>}
                            </div>
                        </div>

                        {(strategy && selectedAe !== "" && Number(selectedAe) > 0 && errors.ap?.message === t("toolfinder.StdParameterForm.valueTooBig")) && <div className="calcErrorBox">
                            {getMaxAPValue(strategy, Number(selectedDiameter), Number(selectedAe)).message}
                        </div>}


                        {/* Submit */}
                        <IonButton style={{ marginTop: 24 }} onClick={handleSubmit(onSubmit)} expand="block">
                            <IonIcon slot="start" icon={arrowDownOutline} />{t("toolfinder.StdParameterForm.submit")}
                        </IonButton>
                    </form>
                </IonCardContent>
            </IonCard>
        )
    } else {
        if (data) {
            return (
                <IonCard color="light" >
                    <IonCardContent>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                            <div>
                                <div>
                                    <b>d<sub>1</sub>:</b> {Number(data.diameter).toLocaleString()} mm <br />
                                    <b>a<sub>e</sub></b>: {Number(data.ae).toLocaleString()} mm <b>a<sub>p</sub></b>: {Number(data.ap).toLocaleString()} mm<br />
                                </div>
                            </div>
                            <div>
                                <IonButton fill="outline" onClick={editBtnCallback}>
                                    <IonIcon icon={createOutline} slot="icon-only" />
                                </IonButton>
                            </div>
                        </div>
                    </IonCardContent>
                </IonCard>
            )
        } else {
            return null
        }

    }

}

export default StandardParameterForm;